import {
    AccessTime as AccessTimeIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    GraphicEq as GraphicEqIcon,
    Home as HomeIcon,
    MonitorHeart as MonitorHeartIcon,
    Movie as MovieIcon,
    Refresh as RefreshIcon,
    StopCircle as StopCircleIcon,
    Translate as TranslateIcon,
} from '@mui/icons-material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import Swal from 'sweetalert2';
import { default as StatusService, default as statusServiceInstance } from '../../services/StatusService';
import VideoCRUDService from '../../services/VideoCRUDService';
import { isAdmin } from '../../utils/authUtils';
import { formatProcessingTime } from '../../utils/formatUtils';
import { PageHeader } from '../common/PageHeader';

const ProcessingStatusDashboard = ({ darkMode, toggleDarkMode, themeVariant }) => {
    // Update state to handle three different types of processing data
    const [textProcessingData, setTextProcessingData] = useState([]);
    const [s2ttProcessingData, setS2ttProcessingData] = useState([]);
    const [t2ttProcessingData, setT2ttProcessingData] = useState([]);
    const [dubbingProcessingData, setDubbingProcessingData] = useState([]);
    const [bookProcessingData, setBookProcessingData] = useState([]);
    const [activeTab, setActiveTab] = useState('s2tt'); // Changed default to 's2tt' instead of 'text'
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeframe, setTimeframe] = useState('24h');
    const [status, setStatus] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [refreshKey, setRefreshKey] = useState(0);
    // Update stats structure to support three different types
    const [stats, setStats] = useState({
        text: {
            total: 0,
            completed: 0,
            inProgress: 0,
            avgProcessingTime: 0,
            totalProcessingTime: 0,
            totalFileSize: 0, // Add total file size for text
        },
        s2tt: {
            total: 0,
            completed: 0,
            inProgress: 0,
            avgProcessingTime: 0,
            avgVideoProcessingTime: 0,
            avgTranslationTime: 0,
            totalVideoProcessingTime: 0,
            totalTranslationTime: 0,
            totalE2ETime: 0,
            totalDuration: 0, // Add total duration for S2TT
        },
        t2tt: {
            total: 0,
            completed: 0,
            inProgress: 0,
            avgProcessingTime: 0,
            totalProcessingTime: 0,
            totalDuration: 0, // Add total duration for T2TT
            totalFileSize: 0, // Add total file size for T2TT
        },
        dubbing: {
            total: 0,
            completed: 0,
            inProgress: 0,
            avgProcessingTime: 0,
            totalProcessingTime: 0,
        },
        book: {
            total: 0,
            completed: 0,
            inProgress: 0,
            avgProcessingTime: 0,
            totalProcessingTime: 0,
        },
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info',
    });
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('processingStartedAt');
    const [selectedUser, setSelectedUser] = useState('all');
    const [users, setUsers] = useState([]);
    const [graphGranularity, setGraphGranularity] = useState('days');
    const navigate = useNavigate();

    useEffect(() => {
        fetchProcessingStatus();

        // Set up auto refresh every minute
        const refreshInterval = setInterval(() => {
            setRefreshKey((prev) => prev + 1);
        }, 60000);

        return () => clearInterval(refreshInterval);
    }, [timeframe, status, refreshKey, activeTab, selectedUser]);

    const getActiveData = () => {
        switch (activeTab) {
            case 'text':
                return textProcessingData;
            case 's2tt':
                return s2ttProcessingData;
            case 't2tt':
                return t2ttProcessingData;
            case 'dubbing':
                return dubbingProcessingData;
            case 'book':
                return bookProcessingData;
            default:
                return [];
        }
    };

    // Determine which stats to use based on active tab
    const getActiveStats = () => {
        return stats[activeTab] || {};
    };

    const currentStats = getActiveStats();

    const videos = getActiveData();

    const fetchProcessingStatus = async () => {
        try {
            setLoading(true);
            setError(null);

            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));

            // Make a single API call to get all processing data
            const response = await StatusService.getProcessingStatus(
                timeframe,
                status === 'all' ? null : status,
                authUser.email,
                authToken,
                selectedUser !== 'all' ? selectedUser : null,
            );

            if (response && response.data) {
                // Sort ALL data into categories FIRST (before user filtering)
                // This is needed to extract all available users for the dropdown
                const allTextItems = [];
                const allS2ttItems = [];
                const allT2ttItems = [];
                const allDubbingItems = [];
                const allBookItems = [];

                response.data.forEach((item) => {
                    if (item.fileType === 'text') {
                        allTextItems.push(item);
                    } else if (item.fileType === 'dubbing') {
                        allDubbingItems.push(item);
                    } else if (item.fileType === 'book') {
                        allBookItems.push(item);
                    } else if (item.fileType === 'video') {
                        const hasT2TTTranslations = item.languages?.some((lang) => lang.translationType === 'T2TT');
                        if (hasT2TTTranslations || item.latestTranslationType === 'T2TT') {
                            allT2ttItems.push(item);
                        } else {
                            allS2ttItems.push(item);
                        }
                    }
                });

                // Extract unique users from ALL data in the ACTIVE TAB (before user filtering)
                // This ensures users dropdown shows all available users, not just the selected one
                let allActiveTabData = [];
                switch (activeTab) {
                    case 'text':
                        allActiveTabData = allTextItems;
                        break;
                    case 's2tt':
                        allActiveTabData = allS2ttItems;
                        break;
                    case 't2tt':
                        allActiveTabData = allT2ttItems;
                        break;
                    case 'dubbing':
                        allActiveTabData = allDubbingItems;
                        break;
                    case 'book':
                        allActiveTabData = allBookItems;
                        break;
                    default:
                        allActiveTabData = [];
                }

                const uniqueUsersSet = new Set();
                allActiveTabData.forEach((item) => {
                    if (item.createdBy) {
                        uniqueUsersSet.add(item.createdBy);
                    }
                });

                // Convert Set to array and create user objects
                const extractedUsers = Array.from(uniqueUsersSet).map((email) => ({
                    email,
                    firstName: null,
                    lastName: null,
                }));

                // Sort users alphabetically by email
                extractedUsers.sort((a, b) => a.email.localeCompare(b.email));

                // Always update users list to sync with timeframe and tab changes
                setUsers(extractedUsers);

                // If the currently selected user is not in the new list, reset to 'all'
                if (selectedUser !== 'all' && !uniqueUsersSet.has(selectedUser)) {
                    setSelectedUser('all');
                }

                // NOW apply user filtering for display
                let filteredData = response.data;
                if (selectedUser !== 'all') {
                    filteredData = response.data.filter((item) => item.createdBy === selectedUser);
                }

                // Sort filtered data into the three categories for display
                const textItems = [];
                const s2ttItems = [];
                const t2ttItems = [];
                const dubbingItems = [];
                const bookItems = [];

                filteredData.forEach((item) => {
                    console.log('Processing item:', item);

                    // Text items have fileType === 'text'
                    if (item.fileType === 'text') {
                        // Check if it has translations, otherwise it's just a raw text item
                        textItems.push(item);
                    }
                    // Dubbing transformation items
                    else if (item.fileType === 'dubbing') {
                        dubbingItems.push(item);
                    }
                    // IDML book translation items
                    else if (item.fileType === 'book') {
                        bookItems.push(item);
                    }
                    // Video items (S2TT or T2TT based on translation type)
                    else if (item.fileType === 'video') {
                        const hasT2TTTranslations = item.languages?.some((lang) => lang.translationType === 'T2TT');

                        if (hasT2TTTranslations || item.latestTranslationType === 'T2TT') {
                            t2ttItems.push(item);
                        } else {
                            s2ttItems.push(item);
                        }
                    }
                });

                console.log('Text items (detailed):', textItems);
                console.log('S2TT items (detailed):', s2ttItems);
                console.log('T2TT items (detailed):', t2ttItems);

                console.log('Text items:', textItems.length);
                console.log('S2TT items:', s2ttItems.length);
                console.log('T2TT items:', t2ttItems.length);
                console.log('Dubbing items:', dubbingItems.length);

                // Process each category with the appropriate processor
                const processedTextData = processTextData(textItems);
                const processedS2TTData = processS2TTData(s2ttItems);
                const processedT2TTData = processT2TTData(t2ttItems);
                const processedDubbingData = processDubbingData(dubbingItems);
                const processedBookData = processBookData(bookItems);

                // Update state for each category
                setTextProcessingData(processedTextData);
                setS2ttProcessingData(processedS2TTData);
                setT2ttProcessingData(processedT2TTData);
                setDubbingProcessingData(processedDubbingData);
                setBookProcessingData(processedBookData);

                // Calculate stats for the active tab
                switch (activeTab) {
                    case 'text':
                        calculateTextStats(processedTextData);
                        break;
                    case 's2tt':
                        calculateS2TTStats(processedS2TTData);
                        break;
                    case 't2tt':
                        calculateT2TTStats(processedT2TTData);
                        break;
                    case 'dubbing':
                        calculateDubbingStats(processedDubbingData);
                        break;
                    case 'book':
                        calculateBookStats(processedBookData);
                        break;
                    default:
                        break;
                }
            }
        } catch (err) {
            console.error(`Error fetching processing status:`, err);
            setError(`Failed to fetch processing status. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes || isNaN(bytes)) return 'N/A';

        if (bytes < 1024) return bytes + ' B';
        else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        else if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        else return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    };

    // Status color mapping function
    const getStatusColor = (status) => {
        if (!status) return 'text.secondary';

        status = status.toUpperCase();

        if (status === 'READY' || status === 'COMPLETED') {
            return 'success.main';
        } else if (status === 'FAILED' || status === 'TERMINATED') {
            return 'error.main';
        } else if (status === 'PROCESSING' || status === 'IN_PROGRESS') {
            return 'primary.main';
        } else {
            return 'text.secondary';
        }
    };

    const processS2TTData = (data) => {
        // Use the existing video processing logic
        return data.map((video) => {
            // Add time information to languages
            const languagesWithTimes = (video.languages || []).map((lang) => ({
                ...lang,
                translationTimeMinutes: calculateTimeDifference(lang.translationStartedAt, lang.translationCompletedAt),
            }));

            // Calculate the video processing time (separate from translation)
            const withLanguages = {
                ...video,
                languages: languagesWithTimes,
                isProcessingComplete: !!video.processingCompletedAt,
            };

            // Calculate video processing time vs translation time - only for completed stages
            const videoProcessingTimeMinutes = calculateVideoProcessingTime(withLanguages);
            const translationTimeMinutes = calculateTranslationTime(withLanguages.languages);

            // Total processing time should only be calculated if both video processing and translations are complete
            let totalProcessingTimeMinutes = null;
            const isVideoProcessingComplete = !!video.processingCompletedAt;
            const areTranslationsComplete = areAllTranslationsComplete(withLanguages);

            if (isVideoProcessingComplete && areTranslationsComplete) {
                totalProcessingTimeMinutes = (videoProcessingTimeMinutes || 0) + (translationTimeMinutes || 0);
            }

            return {
                ...withLanguages,
                videoProcessingTimeMinutes,
                translationTimeMinutes,
                totalProcessingTimeMinutes,
                isFullyComplete: isVideoProcessingComplete && areTranslationsComplete,
            };
        });
    };

    const processT2TTData = (data) => {
        // Process text-to-text translation data
        return data.map((item) => {
            // Find the source language to get its text size
            let sourceTextSize = 0;
            const processedLanguages = (item.languages || []).map((lang) => {
                const processed = {
                    ...lang,
                    translationTimeMinutes: calculateTimeDifference(
                        lang.translationStartedAt,
                        lang.translationCompletedAt,
                    ),
                };

                // If this is the source language, store its text size
                if (lang.isSource) {
                    sourceTextSize = lang.fileSize || 0;
                }

                return processed;
            });

            return {
                ...item,
                processingTimeMinutes: calculateTimeDifference(item.processingStartedAt, item.processingCompletedAt),
                isProcessingComplete: !!item.processingCompletedAt,
                languages: processedLanguages,
                sourceTextSize: sourceTextSize, // Add the source text size to the item
            };
        });
    };

    // Update the calculateTimeDifference function to handle incomplete processes
    const calculateTimeDifference = (startDate, endDate) => {
        if (!startDate) return null;

        // If we explicitly want to calculate time for incomplete processes, we can use current time
        // Otherwise, only calculate time for completed processes
        const end = endDate ? new Date(endDate) : null;
        if (!end) return null; // Return null for incomplete processes instead of using current time

        const start = new Date(startDate);

        try {
            // Calculate difference in milliseconds
            const diffMs = end - start;

            // Convert to minutes with decimal precision for seconds
            return diffMs / (1000 * 60);
        } catch (e) {
            console.error('Error calculating time difference:', e);
            return null;
        }
    };

    const processTextData = (data) => {
        // Process text transcription data
        return data.map((item) => {
            // Handle the specific structure of text items
            // Extract languages from translations object
            const languages = [];
            let sourceLanguageFileSize = 0;

            if (item.translations) {
                for (const [name, details] of Object.entries(item.translations)) {
                    const isSource = details.translationStatus === 'SOURCE_LANGUAGE';

                    // If this is the source language, capture its file size
                    if (isSource && details.fileSize) {
                        sourceLanguageFileSize = details.fileSize;
                    }

                    languages.push({
                        name,
                        status: details.translationStatus,
                        translationStartedAt: details.translationStartedAt,
                        translationCompletedAt: details.translationCompletedAt,
                        isSource: isSource,
                        fileSize: details.fileSize,
                    });
                }
            }

            // Find the first started and last completed timestamps
            const startTimes = languages
                .filter((lang) => lang.translationStartedAt)
                .map((lang) => new Date(lang.translationStartedAt).getTime());

            const endTimes = languages
                .filter((lang) => lang.translationCompletedAt)
                .map((lang) => new Date(lang.translationCompletedAt).getTime());

            const processingStartedAt =
                startTimes.length > 0 ? new Date(Math.min(...startTimes)).toISOString() : item.updatedAt;

            const processingCompletedAt = endTimes.length > 0 ? new Date(Math.max(...endTimes)).toISOString() : null;

            return {
                ...item,
                languages,
                processingStartedAt,
                processingCompletedAt,
                createdBy: item.createdBy || '',
                processingTimeMinutes: calculateTimeDifference(processingStartedAt, processingCompletedAt),
                isProcessingComplete: endTimes.length > 0 && endTimes.length === languages.length - 1, // Exclude source language
                sourceLanguageFileSize: sourceLanguageFileSize, // Add the source language file size
            };
        });
    };

    const processBookData = (data) => {
        // Process IDML book translation data (mirrors processTextData's translations map handling)
        return data.map((item) => {
            const languages = [];

            if (item.translations) {
                for (const [name, details] of Object.entries(item.translations)) {
                    languages.push({
                        name,
                        status: details.translationStatus,
                        translationStartedAt: details.translationStartedAt,
                        translationCompletedAt: details.translationCompletedAt,
                        isSource: false,
                    });
                }
            }

            const startTimes = languages
                .filter((lang) => lang.translationStartedAt)
                .map((lang) => new Date(lang.translationStartedAt).getTime());

            const endTimes = languages
                .filter((lang) => lang.translationCompletedAt)
                .map((lang) => new Date(lang.translationCompletedAt).getTime());

            const processingStartedAt =
                startTimes.length > 0 ? new Date(Math.min(...startTimes)).toISOString() : item.createdAt;

            const processingCompletedAt = endTimes.length > 0 ? new Date(Math.max(...endTimes)).toISOString() : null;

            return {
                ...item,
                languages,
                processingStartedAt,
                processingCompletedAt,
                createdBy: item.createdBy || '',
                processingTimeMinutes: calculateTimeDifference(processingStartedAt, processingCompletedAt),
                isProcessingComplete: !!item.isProcessingComplete,
            };
        });
    };

    // Update the calculateVideoProcessingTime function
    const calculateVideoProcessingTime = (video) => {
        if (!video.processingStartedAt) return null;

        // Only calculate time if processing is completed
        if (!video.processingCompletedAt) return null;

        return calculateTimeDifference(video.processingStartedAt, video.processingCompletedAt);
    };

    // Update the calculateTranslationTime function
    const calculateTranslationTime = (languages) => {
        if (!languages || languages.length === 0) return null;

        const completedTranslations = languages.filter(
            (lang) => lang.translationStartedAt && lang.translationCompletedAt,
        );

        if (completedTranslations.length === 0) {
            return null; // Don't show average time if no translations are complete
        }

        // Calculate average time for completed translations only
        const totalTimeMinutes = completedTranslations.reduce(
            (sum, lang) => sum + (lang.translationTimeMinutes || 0),
            0,
        );

        return totalTimeMinutes / completedTranslations.length;
    };

    // Add a helper function to determine if all languages are complete
    const areAllTranslationsComplete = (video) => {
        if (!video.languages || video.languages.length === 0) return false;

        return video.languages.every(
            (lang) => lang.translationCompletedAt || lang.status === 'COMPLETED' || lang.status === 'READY',
        );
    };

    // Show processing timeline for the selected video
    const [selectedVideo, setSelectedVideo] = useState(null);

    const handleTerminateTranslation = async (video, language) => {
        try {
            const status = await statusServiceInstance.getServerStatus();

            // if video.processingStartedAt is later than status.data.serverStartTime then do not
            // since this means that threads will not be terminated

            if (
                video.processingStartedAt &&
                new Date(video.processingStartedAt) > new Date(status.data.serverStartTime)
            ) {
                setSnackbar({
                    open: true,
                    message: `Translation for ${language.name} in video "${video.name}" started after server start time. Termination not allowed.`,
                    severity: 'error',
                });
                return;
            }

            const result = await Swal.fire({
                title: 'Terminate Translation?',
                text: `Are you sure you want to terminate the translation for ${language.name} in video "${video.name}"?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, terminate it!',
            });

            if (result.isConfirmed) {
                await VideoCRUDService.terminateTranslation(video.id, language.name);

                language.status = 'TERMINATED'; // Update the language status locally

                setSnackbar({
                    open: true,
                    message: `Successfully terminated translation for ${language.name}`,
                    severity: 'success',
                });

                // Refresh the dashboard
                fetchProcessingStatus();
            }
        } catch (error) {
            console.error('Error terminating translation:', error);
            setSnackbar({
                open: true,
                message: `Failed to terminate translation: ${error.message}`,
                severity: 'error',
            });
        }
    };

    const renderLanguageStatus = (video, language) => {
        // Add explicit status check for both PROCESSING and processing
        const isProcessing = language.status !== 'READY' && language.status !== 'TERMINATED';

        return (
            <Box>
                {isProcessing && isAdmin && (
                    <>
                        <Tooltip title="Terminate Translation">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleTerminateTranslation(video, language)}
                                sx={{ ml: 1 }}
                            >
                                <StopCircleIcon />
                            </IconButton>
                        </Tooltip>

                        <Typography variant="body2" fontWeight="medium">
                            {language.name} Translation {language.status}
                        </Typography>
                    </>
                )}
                {!isProcessing && (
                    <Typography variant="body2" color={getStatusColor(language.status)}>
                        {language.name} Translation {language.status}
                    </Typography>
                )}
            </Box>
        );
    };

    // Add new helper function to format video duration
    const formatDuration = (duration) => {
        if (!duration) return 'N/A';
        // Format HH:MM:SS.ms to a more readable format
        // Remove milliseconds if present
        const formattedDuration = duration.split('.')[0];
        return formattedDuration;
    };

    // Add this sorting function
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Add this comparator function for sorting
    const descendingComparator = (a, b, orderBy) => {
        // Handle special cases for calculated fields
        if (orderBy === 'translationTime') {
            const aValue = calculateTranslationTime(a.languages) || 0;
            const bValue = calculateTranslationTime(b.languages) || 0;
            return bValue - aValue;
        }

        if (orderBy === 'totalTime') {
            return (b.totalProcessingTimeMinutes || 0) - (a.totalProcessingTimeMinutes || 0);
        }

        if (orderBy === 'videoProcessing') {
            return (b.videoProcessingTimeMinutes || 0) - (a.videoProcessingTimeMinutes || 0);
        }

        // Duration field needs special handling as it's a string like "00:01:04.78"
        if (orderBy === 'duration') {
            const aDuration = a.duration ? a.duration.split(':').reduce((acc, time) => 60 * acc + +time, 0) : 0;
            const bDuration = b.duration ? b.duration.split(':').reduce((acc, time) => 60 * acc + +time, 0) : 0;
            return bDuration - aDuration;
        }

        // Standard fields comparison
        if (b[orderBy] === undefined || b[orderBy] === null) return -1;
        if (a[orderBy] === undefined || a[orderBy] === null) return 1;

        if (typeof b[orderBy] === 'string') {
            return b[orderBy].localeCompare(a[orderBy]);
        }

        return b[orderBy] - a[orderBy];
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    // Add a custom sort function for the table
    const sortedVideos = [...getActiveData()].sort(getComparator(order, orderBy));

    // Add this component for sortable column headers
    const SortableTableCell = ({ id, label, width }) => {
        const isActive = orderBy === id;
        return (
            <TableCell
                width={width}
                sortDirection={isActive ? order : false}
                onClick={() => handleRequestSort(id)}
                sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)' },
                    position: 'relative',
                    fontWeight: isActive ? 'bold' : 'normal',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {label}
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5 }}>
                        {isActive ? (
                            order === 'asc' ? (
                                '↑'
                            ) : (
                                '↓'
                            )
                        ) : (
                            <span style={{ color: 'rgba(127, 127, 127, 0.5)' }}>↕</span>
                        )}
                    </Box>
                </Box>
            </TableCell>
        );
    };

    const handleRefresh = () => {
        setRefreshKey((prev) => prev + 1);
    };

    // Update calculateTextStats to include file size total
    const calculateTextStats = (data) => {
        const totalItems = data.length;
        const completedItems = data.filter((item) => item.isProcessingComplete).length;
        const inProgressItems = totalItems - completedItems;

        // Calculate processing times only for completed items
        const completedItemsWithTime = data.filter((item) => item.isProcessingComplete && item.processingTimeMinutes);

        const totalProcessingTime = completedItemsWithTime.reduce((sum, item) => sum + item.processingTimeMinutes, 0);

        const avgProcessingTime =
            completedItemsWithTime.length > 0 ? totalProcessingTime / completedItemsWithTime.length : 0;

        // Calculate total file size using source language file size when available
        const totalFileSize = data.reduce((sum, item) => {
            // Use source language file size if available, otherwise fall back to item.fileSize
            return sum + (item.sourceLanguageFileSize || item.fileSize || 0);
        }, 0);

        setStats((prev) => ({
            ...prev,
            text: {
                total: totalItems,
                completed: completedItems,
                inProgress: inProgressItems,
                avgProcessingTime,
                totalProcessingTime,
                totalFileSize,
            },
        }));
    };

    // Update calculateS2TTStats to include total duration
    const calculateS2TTStats = (data) => {
        const totalVideos = data.length;
        const completedVideos = data.filter((video) => video.isFullyComplete).length;
        const inProgressVideos = totalVideos - completedVideos;

        // Calculate times for completed videos
        const completedVideosWithTime = data.filter(
            (video) => video.isFullyComplete && video.totalProcessingTimeMinutes,
        );

        // Calculate video processing times
        const videosWithProcessingTime = data.filter((video) => video.videoProcessingTimeMinutes);

        const totalVideoProcessingTime = videosWithProcessingTime.reduce(
            (sum, video) => sum + video.videoProcessingTimeMinutes,
            0,
        );

        const avgVideoProcessingTime =
            videosWithProcessingTime.length > 0 ? totalVideoProcessingTime / videosWithProcessingTime.length : 0;

        // Calculate translation times
        const videosWithTranslationTime = data.filter((video) => video.translationTimeMinutes);

        const totalTranslationTime = videosWithTranslationTime.reduce(
            (sum, video) => sum + video.translationTimeMinutes,
            0,
        );

        const avgTranslationTime =
            videosWithTranslationTime.length > 0 ? totalTranslationTime / videosWithTranslationTime.length : 0;

        // Calculate total E2E times
        const totalE2ETime = completedVideosWithTime.reduce((sum, video) => sum + video.totalProcessingTimeMinutes, 0);

        const avgProcessingTime =
            completedVideosWithTime.length > 0 ? totalE2ETime / completedVideosWithTime.length : 0;

        // Calculate total duration in seconds
        const totalDurationInSeconds = data.reduce((sum, video) => {
            if (!video.duration) return sum;

            // Parse HH:MM:SS format to seconds
            const parts = video.duration.split(':');
            if (parts.length !== 3) return sum;

            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            const seconds = parseFloat(parts[2]);

            return sum + (hours * 3600 + minutes * 60 + seconds);
        }, 0);

        setStats((prev) => ({
            ...prev,
            s2tt: {
                total: totalVideos,
                completed: completedVideos,
                inProgress: inProgressVideos,
                avgProcessingTime,
                avgVideoProcessingTime,
                avgTranslationTime,
                totalVideoProcessingTime,
                totalTranslationTime,
                totalE2ETime,
                totalDuration: totalDurationInSeconds, // Add this line
            },
        }));
    };

    // Update calculateT2TTStats to include total duration and file size
    const calculateT2TTStats = (data) => {
        const totalItems = data.length;
        const completedItems = data.filter((item) => item.isProcessingComplete).length;
        const inProgressItems = totalItems - completedItems;

        // Calculate processing times only for completed items
        const completedItemsWithTime = data.filter((item) => item.isProcessingComplete && item.processingTimeMinutes);

        const totalProcessingTime = completedItemsWithTime.reduce((sum, item) => sum + item.processingTimeMinutes, 0);

        const avgProcessingTime =
            completedItemsWithTime.length > 0 ? totalProcessingTime / completedItemsWithTime.length : 0;

        // Calculate total file size using sourceTextSize instead of fileSize
        const totalFileSize = data.reduce((sum, item) => sum + (item.sourceTextSize || item.fileSize || 0), 0);

        // Calculate total duration in seconds
        const totalDurationInSeconds = data.reduce((sum, item) => {
            if (!item.duration) return sum;

            // Parse HH:MM:SS format to seconds
            const parts = item.duration.split(':');
            if (parts.length !== 3) return sum;

            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            const seconds = parseFloat(parts[2]);

            return sum + (hours * 3600 + minutes * 60 + seconds);
        }, 0);

        setStats((prev) => ({
            ...prev,
            t2tt: {
                total: totalItems,
                completed: completedItems,
                inProgress: inProgressItems,
                avgProcessingTime,
                totalProcessingTime,
                totalDuration: totalDurationInSeconds, // Add this line
                totalFileSize: totalFileSize, // Use the source text size total
            },
        }));
    };

    const processDubbingData = (data) => {
        return data.map((item) => ({
            ...item,
            processingTimeMinutes: calculateTimeDifference(item.dubbingStartedAt, item.dubbingCompletedAt),
            isProcessingComplete: item.dubbingStatus === 'COMPLETED',
            processingStartedAt: item.dubbingStartedAt,
            processingCompletedAt: item.dubbingCompletedAt,
            dubbingAudioExpired: !!item.dubbingAudioExpired,
        }));
    };

    const calculateDubbingStats = (data) => {
        const totalItems = data.length;
        const completedItems = data.filter((item) => item.isProcessingComplete).length;
        const inProgressItems = data.filter((item) => !item.isProcessingComplete && !item.dubbingAudioExpired).length;
        const expiredItems = data.filter((item) => item.dubbingAudioExpired).length;

        const completedItemsWithTime = data.filter((item) => item.isProcessingComplete && item.processingTimeMinutes);
        const totalProcessingTime = completedItemsWithTime.reduce((sum, item) => sum + item.processingTimeMinutes, 0);
        const avgProcessingTime =
            completedItemsWithTime.length > 0 ? totalProcessingTime / completedItemsWithTime.length : 0;

        setStats((prev) => ({
            ...prev,
            dubbing: {
                total: totalItems,
                completed: completedItems,
                inProgress: inProgressItems,
                expired: expiredItems,
                avgProcessingTime,
                totalProcessingTime,
                totalE2ETime: totalProcessingTime,
            },
        }));
    };
    const calculateBookStats = (data) => {
        const totalItems = data.length;
        const completedItems = data.filter((item) => item.isProcessingComplete).length;
        const inProgressItems = totalItems - completedItems;

        const completedItemsWithTime = data.filter((item) => item.isProcessingComplete && item.processingTimeMinutes);
        const totalProcessingTime = completedItemsWithTime.reduce((sum, item) => sum + item.processingTimeMinutes, 0);
        const avgProcessingTime =
            completedItemsWithTime.length > 0 ? totalProcessingTime / completedItemsWithTime.length : 0;

        setStats((prev) => ({
            ...prev,
            book: {
                total: totalItems,
                completed: completedItems,
                inProgress: inProgressItems,
                avgProcessingTime,
                totalProcessingTime,
            },
        }));
    };

    // Add before the return statement

    const renderTableBody = () => {
        const data = sortedVideos;
        return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => {
            switch (activeTab) {
                case 'text':
                    return renderTextRow(item);
                case 's2tt':
                    return renderVideoRow(item);
                case 't2tt':
                    return renderT2TTRow(item);
                case 'dubbing':
                    return renderDubbingRow(item);
                case 'book':
                    return renderBookRow(item);
                default:
                    return null;
            }
        });
    };

    // Add this function to format total duration (in seconds) to HH:MM:SS format
    const formatTotalDuration = (totalSeconds) => {
        if (!totalSeconds || isNaN(totalSeconds)) return 'N/A';

        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = Math.floor(totalSeconds % 60);

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
            .toString()
            .padStart(2, '0')}`;
    };

    // Format date in a human-readable way
    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';

        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }).format(date);
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid date';
        }
    };

    // Get time elapsed since the given date
    const getTimeSince = (dateString) => {
        if (!dateString) return 'N/A';

        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;

            // Convert to minutes, hours, or days as appropriate
            const diffMinutes = Math.floor(diffMs / (1000 * 60));

            if (diffMinutes < 60) {
                return `${diffMinutes} min${diffMinutes !== 1 ? 's' : ''} ago`;
            }

            const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
            if (diffHours < 24) {
                return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
            }

            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
        } catch (error) {
            console.error('Error calculating time since:', error);
            return 'Unknown';
        }
    };

    // Handle page change for pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change for pagination
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Render a video row for the S2TT table
    const renderVideoRow = (video) => (
        <TableRow key={video.id} hover onClick={() => setSelectedVideo(video)} sx={{ cursor: 'pointer' }}>
            <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                    {video.name}
                </Typography>
            </TableCell>
            <TableCell>{video.createdBy ? video.createdBy.replace(/@amrutvaani\.org/g, '') : ''}</TableCell>
            <TableCell>
                <Typography variant="body2">{formatDuration(video.duration)}</Typography>
            </TableCell>

            <TableCell>
                <Tooltip title={formatDateTime(video.processingStartedAt)}>
                    <Typography variant="body2">{getTimeSince(video.processingStartedAt)}</Typography>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Typography variant="body2">{formatProcessingTime(video.videoProcessingTimeMinutes)}</Typography>
                <Typography variant="caption" color={video.isProcessingComplete ? 'success.main' : 'text.secondary'}>
                    {video.isProcessingComplete ? 'Completed' : 'In Progress'}
                </Typography>
            </TableCell>
            <TableCell>
                <Box>
                    {video.languages &&
                        video.languages.map((lang, index) => (
                            <Chip
                                key={index}
                                label={lang.name}
                                size="small"
                                color={lang.status === 'READY' ? 'success' : 'primary'}
                                sx={{ mr: 0.5, mb: 0.5 }}
                            />
                        ))}
                    {!video.languages || video.languages.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                            No languages
                        </Typography>
                    ) : null}
                </Box>
            </TableCell>
            <TableCell>
                <Typography variant="body2">{formatProcessingTime(video.translationTimeMinutes)}</Typography>
            </TableCell>
            <TableCell>
                <Typography variant="body2">{formatProcessingTime(video.totalProcessingTimeMinutes)}</Typography>
                {video.isFullyComplete ? (
                    <Chip label="Complete" color="success" size="small" />
                ) : (
                    <Chip label="In Progress" color="primary" size="small" />
                )}
            </TableCell>
        </TableRow>
    );

    // Render a T2TT row for the text-to-text translation table
    const renderT2TTRow = (item) => {
        // Find source language for display purposes
        const sourceLanguage = item.languages?.find((lang) => lang.isSource);
        const sourceTextSize = sourceLanguage?.textSize || item.sourceTextSize || item.fileSize;

        return (
            <TableRow key={item.id} hover onClick={() => setSelectedItem(item)} sx={{ cursor: 'pointer' }}>
                <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                        {item.name}
                    </Typography>
                </TableCell>
                <TableCell>{item.createdBy ? item.createdBy.replace(/@amrutvaani\.org/g, '') : ''}</TableCell>
                <TableCell>
                    <Typography variant="body2">{formatDuration(item.duration)}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">
                        {formatFileSize(sourceTextSize)}
                        {sourceLanguage && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                ({sourceLanguage.name})
                            </Typography>
                        )}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Tooltip title={formatDateTime(item.processingStartedAt)}>
                        <Typography variant="body2">{getTimeSince(item.processingStartedAt)}</Typography>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Box>
                        {item.languages &&
                            item.languages.map((lang, index) => (
                                <Chip
                                    key={index}
                                    label={lang.name + (lang.isSource ? ' (Source)' : '')}
                                    size="small"
                                    color={lang.status === 'READY' ? 'success' : 'primary'}
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                        {!item.languages || item.languages.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                                No languages
                            </Typography>
                        ) : null}
                    </Box>
                </TableCell>
                <TableCell>
                    <Chip
                        label={item.isProcessingComplete ? 'Completed' : 'In Progress'}
                        color={item.isProcessingComplete ? 'success' : 'primary'}
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{formatProcessingTime(item.processingTimeMinutes)}</Typography>
                </TableCell>
            </TableRow>
        );
    };

    const renderTextRow = (item) => {
        // Find source language for display purposes
        const sourceLanguage = item.languages?.find((lang) => lang.isSource);
        const sourceFileSize = sourceLanguage?.fileSize || item.sourceLanguageFileSize || item.fileSize;

        return (
            <TableRow key={item.id} hover onClick={() => setSelectedItem(item)} sx={{ cursor: 'pointer' }}>
                <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                        {item.name}
                    </Typography>
                </TableCell>
                <TableCell>{item.createdBy ? item.createdBy.replace(/@amrutvaani\.org/g, '') : ''}</TableCell>
                <TableCell>
                    <Typography variant="body2">
                        {formatFileSize(sourceFileSize)}
                        {sourceLanguage && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                ({sourceLanguage.name})
                            </Typography>
                        )}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Tooltip title={formatDateTime(item.processingStartedAt)}>
                        <Typography variant="body2">{getTimeSince(item.processingStartedAt)}</Typography>
                    </Tooltip>
                </TableCell>
                <TableCell>
                    <Chip
                        label={item.isProcessingComplete ? 'Completed' : 'In Progress'}
                        color={item.isProcessingComplete ? 'success' : 'primary'}
                        size="small"
                    />
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{formatProcessingTime(item.processingTimeMinutes)}</Typography>
                </TableCell>
                <TableCell>
                    <Box>
                        {item.languages &&
                            item.languages.map((lang, index) => (
                                <Chip
                                    key={index}
                                    label={lang.name + (lang.isSource ? ' (Source)' : '')}
                                    size="small"
                                    color={lang.status === 'COMPLETED' || lang.isSource ? 'success' : 'primary'}
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    const renderBookRow = (item) => (
        <TableRow key={item.id} hover onClick={() => setSelectedItem(item)} sx={{ cursor: 'pointer' }}>
            <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                    {item.name}
                </Typography>
            </TableCell>
            <TableCell>{item.createdBy ? item.createdBy.replace(/@amrutvaani\.org/g, '') : ''}</TableCell>
            <TableCell>{item.sourceLanguage || 'N/A'}</TableCell>
            <TableCell>
                <Tooltip title={formatDateTime(item.processingStartedAt || item.createdAt)}>
                    <Typography variant="body2">{getTimeSince(item.processingStartedAt || item.createdAt)}</Typography>
                </Tooltip>
            </TableCell>
            <TableCell>
                <Chip
                    label={item.processStage || 'N/A'}
                    color={item.processStage === 'PARSE_FAILED' ? 'error' : 'default'}
                    size="small"
                />
            </TableCell>
            <TableCell>
                <Box>
                    {item.languages &&
                        item.languages.map((lang, index) => (
                            <Chip
                                key={index}
                                label={`${lang.name}: ${lang.status || 'N/A'}`}
                                size="small"
                                color={
                                    lang.status === 'REVIEW' || lang.status === 'EXPORTED' ? 'success' : 'primary'
                                }
                                sx={{ mr: 0.5, mb: 0.5 }}
                            />
                        ))}
                    {!item.languages || item.languages.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                            No languages
                        </Typography>
                    ) : null}
                </Box>
            </TableCell>
            <TableCell>
                <Chip
                    label={item.isProcessingComplete ? 'Completed' : 'In Progress'}
                    color={item.isProcessingComplete ? 'success' : 'primary'}
                    size="small"
                />
            </TableCell>
            <TableCell>
                <Typography variant="body2">{formatProcessingTime(item.processingTimeMinutes)}</Typography>
            </TableCell>
        </TableRow>
    );

    const renderDubbingRow = (item) => (
        <TableRow
            key={item.id}
            hover
            sx={{
                cursor: 'default',
                opacity: item.dubbingAudioExpired ? 0.55 : 1,
            }}
        >
            <TableCell>
                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                    {item.name}
                </Typography>
            </TableCell>
            <TableCell>{item.createdBy ? item.createdBy.replace(/@amrutvaani\.org/g, '') : ''}</TableCell>
            <TableCell>
                <Chip
                    label={item.type === 'video' ? 'Video' : 'Audio'}
                    size="small"
                    color={item.type === 'video' ? 'primary' : 'secondary'}
                />
            </TableCell>
            <TableCell>{item.sourceLanguage || 'N/A'}</TableCell>
            <TableCell>
                <Tooltip title={formatDateTime(item.dubbingStartedAt)}>
                    <Typography variant="body2">{getTimeSince(item.dubbingStartedAt)}</Typography>
                </Tooltip>
            </TableCell>
            <TableCell>
                {item.dubbingAudioExpired ? (
                    <Chip label="Audio Expired" size="small" color="default" variant="outlined" />
                ) : (
                    <Chip
                        label={item.isProcessingComplete ? 'Completed' : item.dubbingStatus || 'In Progress'}
                        color={
                            item.dubbingStatus === 'COMPLETED'
                                ? 'success'
                                : item.dubbingStatus === 'FAILED'
                                ? 'error'
                                : 'primary'
                        }
                        size="small"
                    />
                )}
                {!item.dubbingAudioExpired && item.dubbingStatus === 'FAILED' && item.failReason && (
                    <Tooltip title={item.failReason}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mt: 0.5 }}
                        >
                            {item.failReason}
                        </Typography>
                    </Tooltip>
                )}
            </TableCell>
            <TableCell>
                <Typography variant="body2">{formatProcessingTime(item.processingTimeMinutes)}</Typography>
            </TableCell>
        </TableRow>
    );

    const renderTableHeader = () => {
        switch (activeTab) {
            case 'text':
                return (
                    <TableRow>
                        <SortableTableCell id="name" label="Text Name" width="20%" />
                        <SortableTableCell id="createdBy" label="Created By" width="15%" />
                        <SortableTableCell id="fileSize" label="Size" width="10%" />
                        <SortableTableCell id="processingStartedAt" label="Started" width="15%" />
                        <SortableTableCell id="status" label="Status" width="10%" />
                        <SortableTableCell id="processingTime" label="Processing Time" width="15%" />
                        <SortableTableCell id="languages" label="Languages" width="15%" />
                    </TableRow>
                );
            case 's2tt':
                return (
                    <TableRow>
                        <SortableTableCell id="name" label="Video Name" width="18%" />
                        <SortableTableCell id="createdBy" label="Created By" width="8%" />
                        <SortableTableCell id="duration" label="Duration" width="8%" />
                        <SortableTableCell id="processingStartedAt" label="Started" width="9%" />
                        <SortableTableCell id="videoProcessing" label="Video Processing" width="12%" />
                        <SortableTableCell id="languages" label="Languages" width="16%" />
                        <SortableTableCell id="translationTime" label="Translation Time" width="11%" />
                        <SortableTableCell id="totalTime" label="Total Time" width="10%" />
                    </TableRow>
                );
            case 't2tt':
                return (
                    <TableRow>
                        <SortableTableCell id="name" label="Video Name" width="18%" />
                        <SortableTableCell id="createdBy" label="Created By" width="10%" />
                        <SortableTableCell id="duration" label="Duration" width="8%" />
                        <SortableTableCell id="fileSize" label="Size" width="8%" />
                        <SortableTableCell id="processingStartedAt" label="Started" width="12%" />
                        <SortableTableCell id="languages" label="Languages" width="18%" />
                        <SortableTableCell id="status" label="Status" width="12%" />
                        <SortableTableCell id="processingTime" label="Processing Time" width="14%" />
                    </TableRow>
                );
            case 'dubbing':
                return (
                    <TableRow>
                        <SortableTableCell id="name" label="Name" width="22%" />
                        <SortableTableCell id="createdBy" label="Created By" width="15%" />
                        <SortableTableCell id="type" label="Type" width="10%" />
                        <SortableTableCell id="sourceLanguage" label="Language" width="12%" />
                        <SortableTableCell id="dubbingStartedAt" label="Started" width="15%" />
                        <SortableTableCell id="dubbingStatus" label="Status" width="13%" />
                        <SortableTableCell id="processingTime" label="Processing Time" width="13%" />
                    </TableRow>
                );
            case 'book':
                return (
                    <TableRow>
                        <SortableTableCell id="name" label="Book Name" width="20%" />
                        <SortableTableCell id="createdBy" label="Created By" width="12%" />
                        <SortableTableCell id="sourceLanguage" label="Source Language" width="12%" />
                        <SortableTableCell id="processingStartedAt" label="Started" width="14%" />
                        <SortableTableCell id="processStage" label="Stage" width="10%" />
                        <SortableTableCell id="languages" label="Languages" width="17%" />
                        <SortableTableCell id="status" label="Status" width="8%" />
                        <SortableTableCell id="processingTime" label="Processing Time" width="15%" />
                    </TableRow>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        const getDefaultGranularity = (tf) => {
            if (tf.startsWith('ytd_')) return 'months';
            if (tf === '30d' || tf === 'current_month' || tf.startsWith('month_')) return 'weeks';
            return 'days';
        };
        setGraphGranularity(getDefaultGranularity(timeframe));
    }, [timeframe]);

    const chartData = useMemo(() => {
        let data;
        switch (activeTab) {
            case 'text': data = textProcessingData; break;
            case 's2tt': data = s2ttProcessingData; break;
            case 't2tt': data = t2ttProcessingData; break;
            case 'dubbing': data = dubbingProcessingData; break;
            case 'book': data = bookProcessingData; break;
            default: data = [];
        }
        if (!data || data.length === 0) return [];

        const now = new Date();
        let rangeStart;
        let rangeEnd = new Date(now);

        if (timeframe === '1h') rangeStart = new Date(now - 3600000);
        else if (timeframe === '6h') rangeStart = new Date(now - 6 * 3600000);
        else if (timeframe === '12h') rangeStart = new Date(now - 12 * 3600000);
        else if (timeframe === '24h') rangeStart = new Date(now - 24 * 3600000);
        else if (timeframe === '7d') rangeStart = new Date(now - 7 * 86400000);
        else if (timeframe === '30d') rangeStart = new Date(now - 30 * 86400000);
        else if (timeframe === 'current_month') {
            rangeStart = new Date(now.getFullYear(), now.getMonth(), 1);
        } else if (timeframe.startsWith('month_')) {
            const parts = timeframe.split('_');
            rangeStart = new Date(parseInt(parts[1]), parseInt(parts[2]) - 1, 1);
            rangeEnd = new Date(parseInt(parts[1]), parseInt(parts[2]), 0, 23, 59, 59);
        } else if (timeframe.startsWith('ytd_')) {
            const year = parseInt(timeframe.split('_')[1]);
            rangeStart = new Date(year, 0, 1);
            rangeEnd = year === now.getFullYear() ? now : new Date(year, 11, 31, 23, 59, 59);
        } else {
            rangeStart = new Date(now - 7 * 86400000);
        }

        const getBucketKey = (date) => {
            if (graphGranularity === 'months') {
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            } else if (graphGranularity === 'weeks') {
                const d = new Date(date);
                const day = d.getDay();
                d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
                d.setHours(0, 0, 0, 0);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            } else {
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            }
        };

        const formatLabel = (key) => {
            if (graphGranularity === 'months') {
                const [y, m] = key.split('-');
                return new Intl.DateTimeFormat('en-US', { month: 'short', year: '2-digit' }).format(
                    new Date(parseInt(y), parseInt(m) - 1, 1)
                );
            } else {
                const [y, m, day] = key.split('-');
                return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(
                    new Date(parseInt(y), parseInt(m) - 1, parseInt(day))
                );
            }
        };

        const buckets = new Map();
        const cursor = new Date(rangeStart);

        const emptyBucket = () => ({ total: 0, completed: 0, inProgress: 0, totalDurationSec: 0, totalSizeBytes: 0 });

        if (graphGranularity === 'months') {
            cursor.setDate(1);
            cursor.setHours(0, 0, 0, 0);
            while (cursor <= rangeEnd) {
                buckets.set(getBucketKey(cursor), emptyBucket());
                cursor.setMonth(cursor.getMonth() + 1);
            }
        } else if (graphGranularity === 'weeks') {
            const startDay = cursor.getDay();
            cursor.setDate(cursor.getDate() - (startDay === 0 ? 6 : startDay - 1));
            cursor.setHours(0, 0, 0, 0);
            while (cursor <= rangeEnd) {
                buckets.set(getBucketKey(cursor), emptyBucket());
                cursor.setDate(cursor.getDate() + 7);
            }
        } else {
            cursor.setHours(0, 0, 0, 0);
            while (cursor <= rangeEnd) {
                buckets.set(getBucketKey(cursor), emptyBucket());
                cursor.setDate(cursor.getDate() + 1);
            }
        }

        const parseDurSec = (dur) => {
            if (!dur) return 0;
            const p = dur.split(':');
            if (p.length !== 3) return 0;
            return parseInt(p[0], 10) * 3600 + parseInt(p[1], 10) * 60 + parseFloat(p[2]);
        };

        data.forEach((item) => {
            const dateStr = item.processingStartedAt || item.createdAt || item.updatedAt;
            if (!dateStr) return;
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return;
            const key = getBucketKey(date);
            if (!buckets.has(key)) return;
            const b = buckets.get(key);
            b.total += 1;
            const isComplete = activeTab === 's2tt' ? item.isFullyComplete : item.isProcessingComplete;
            if (isComplete) b.completed += 1;
            else b.inProgress += 1;
            if (activeTab === 's2tt' || activeTab === 't2tt') {
                b.totalDurationSec += parseDurSec(item.duration);
            }
            if (activeTab === 'text') {
                b.totalSizeBytes += (item.sourceLanguageFileSize || item.fileSize || 0);
            }
        });

        return Array.from(buckets.entries()).map(([key, b]) => ({
            label: formatLabel(key),
            total: b.total,
            completed: b.completed,
            inProgress: b.inProgress,
            totalDurationMin: Math.round(b.totalDurationSec / 60),
            totalSizeMB: Math.round((b.totalSizeBytes / (1024 * 1024)) * 100) / 100,
        }));
    }, [activeTab, textProcessingData, s2ttProcessingData, t2ttProcessingData, dubbingProcessingData, bookProcessingData, graphGranularity, timeframe]);

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
            }}
        >
            <Paper
                sx={{
                    borderRadius: 0,
                    padding: 0,
                }}
            >
                <PageHeader
                    darkMode={darkMode}
                    toggleDarkMode={toggleDarkMode}
                    themeVariant={themeVariant}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, padding: 1 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<HomeIcon />}
                        onClick={() => navigate('/')}
                        sx={{ mr: 2 }}
                    >
                        Home
                    </Button>
                    {/* Add arrow icon between buttons */}
                    <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Button variant="text" color="secondary" startIcon={<MonitorHeartIcon />} sx={{ mr: 2 }}>
                        System
                    </Button>
                    <NavigateNextIcon sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }} />
                    <Button variant="text" color="secondary" startIcon={<VideoFileIcon />} sx={{ mr: 2 }}>
                        Processing Status Dashboard
                    </Button>
                </Box>
                {/* Update this Box to fill the width */}
                <Box
                    sx={{
                        mb: 3,
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        width: '100%',
                    }}
                >
                    {/* Update this inner Box to fill the width */}
                    <Box sx={{ mb: 3, width: '100%' }}>
                        {/* Make sure the Paper components also fill the width */}
                        <Paper sx={{ p: 2, mb: 3, width: '100%' }} className="processing-status-summary">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', gap: 2 }} className="processing-filters">
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                        <Select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                                            <MenuItem value="1h">Last hour</MenuItem>
                                            <MenuItem value="6h">Last 6 hours</MenuItem>
                                            <MenuItem value="12h">Last 12 hours</MenuItem>
                                            <MenuItem value="24h">Last 24 hours</MenuItem>
                                            <MenuItem value="7d">Last 7 days</MenuItem>
                                            <MenuItem value="30d">Last 30 days</MenuItem>
                                            <Divider />
                                            {/* Dynamic month options */}
                                            {(() => {
                                                const now = new Date();
                                                const months = [];

                                                // Current month
                                                months.push(
                                                    <MenuItem key="current_month" value="current_month">
                                                        {new Intl.DateTimeFormat('en-US', { month: 'long' }).format(
                                                            now,
                                                        )}{' '}
                                                        {now.getFullYear()}
                                                    </MenuItem>,
                                                );

                                                // Previous 3 months
                                                for (let i = 1; i <= 3; i++) {
                                                    const prevMonth = new Date(
                                                        now.getFullYear(),
                                                        now.getMonth() - i,
                                                        1,
                                                    );
                                                    const monthName = new Intl.DateTimeFormat('en-US', {
                                                        month: 'long',
                                                    }).format(prevMonth);
                                                    const year = prevMonth.getFullYear();

                                                    months.push(
                                                        <MenuItem
                                                            key={`month_${i}`}
                                                            value={`month_${prevMonth.getFullYear()}_${
                                                                prevMonth.getMonth() + 1
                                                            }`}
                                                        >
                                                            {monthName} {year}
                                                        </MenuItem>,
                                                    );
                                                }
                                                // Add previous year's months
                                                months.push(<Divider key="prev-year-divider" />);

                                                // Year to date
                                                months.push(
                                                    <MenuItem key="ytd" value={`ytd_${now.getFullYear()}`}>
                                                        {now.getFullYear()}
                                                    </MenuItem>,
                                                );

                                                // Previous year label
                                                const prevYear = now.getFullYear() - 1;
                                                // Previous year to date option
                                                months.push(
                                                    <MenuItem key="prev_ytd" value={`ytd_${prevYear}`}>
                                                        {prevYear}
                                                    </MenuItem>,
                                                );

                                                return months;
                                            })()}
                                        </Select>
                                    </FormControl>

                                    {/* User Filter Dropdown */}
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                        <Select
                                            value={selectedUser}
                                            onChange={(e) => setSelectedUser(e.target.value)}
                                            displayEmpty
                                        >
                                            <MenuItem value="all">All Users</MenuItem>
                                            {users && users.length > 0 &&
                                                users.map((user) => {
                                                    // Extract username from email (before @)
                                                    const username = user.email.split('@')[0];

                                                    // Split by underscore and capitalize each part
                                                    const nameParts = username.split('_');
                                                    const formattedName = nameParts
                                                        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
                                                        .join(' ');

                                                    return (
                                                        <MenuItem key={user.email} value={user.email}>
                                                            {formattedName}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                    Auto-refreshes every minute
                                </Typography>
                                <Button
                                    startIcon={<RefreshIcon />}
                                    onClick={handleRefresh}
                                    variant="outlined"
                                    size="small"
                                >
                                    Refresh
                                </Button>
                            </Box>

                            {/* Add this after the "Auto-refreshes every minute" text and before the stats cards */}

                            <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={(e, newValue) => setActiveTab(newValue)}
                                    aria-label="processing tabs"
                                >
                                    <Tab
                                        label="Speech to Text Translation"
                                        value="s2tt"
                                        icon={<MovieIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab
                                        label="Text to Text Translation"
                                        value="t2tt"
                                        icon={<TranslateIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab
                                        label="Text Translation"
                                        value="text"
                                        icon={<TextSnippetIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab
                                        label="Dubbing Transformations"
                                        value="dubbing"
                                        icon={<GraphicEqIcon />}
                                        iconPosition="start"
                                    />
                                    <Tab
                                        label="Books"
                                        value="book"
                                        icon={<MenuBookIcon />}
                                        iconPosition="start"
                                    />
                                </Tabs>
                            </Box>

                            {/* Stats cards - single row layout */}
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Card
                                        elevation={1}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    >
                                        <CardContent
                                            sx={{
                                                flex: '1 0 auto',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                            }}
                                        >
                            <Typography variant="subtitle2" color="text.secondary">
                                                {activeTab === 'text' ? 'Text Files' : activeTab === 'dubbing' ? 'Dubbing Transforms' : activeTab === 'book' ? 'Books' : 'Videos'} Status
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    mt: 0.5,
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box sx={{ textAlign: 'center', flex: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Total
                                                    </Typography>
                                                    <Typography variant="h6">{currentStats.total}</Typography>
                                                </Box>
                                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                                <Box sx={{ textAlign: 'center', flex: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        In Progress
                                                    </Typography>
                                                    <Typography variant="h6" color="primary.main">
                                                        {currentStats.inProgress}
                                                    </Typography>
                                                </Box>
                                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                                <Box sx={{ textAlign: 'center', flex: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Completed
                                                    </Typography>
                                                    <Typography variant="h6" color="success.main">
                                                        {currentStats.completed}
                                                    </Typography>
                                                </Box>
                                                {activeTab === 'dubbing' && currentStats.expired > 0 && (
                                                    <>
                                                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                                                        <Box sx={{ textAlign: 'center', flex: 1 }}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Expired
                                                            </Typography>
                                                            <Typography variant="h6" color="text.secondary">
                                                                {currentStats.expired}
                                                            </Typography>
                                                        </Box>
                                                    </>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                {/* Separate processing time cards */}
                                <Grid item xs={12} sm={6} md={2}>
                                    <Card
                                        elevation={1}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    >
                                        <CardContent
                                            sx={{
                                                flex: '1 0 auto',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Video Processing
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Download & Compression
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg:
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {currentStats.avgVideoProcessingTime > 0
                                                            ? formatProcessingTime(currentStats.avgVideoProcessingTime)
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Total:
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {currentStats.totalVideoProcessingTime > 0
                                                            ? formatProcessingTime(
                                                                  currentStats.totalVideoProcessingTime,
                                                              )
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <Card
                                        elevation={1}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    >
                                        <CardContent
                                            sx={{
                                                flex: '1 0 auto',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Translation Time
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                S2TT & Translation
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg:
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {currentStats.avgTranslationTime > 0
                                                            ? formatProcessingTime(currentStats.avgTranslationTime)
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Total:
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {currentStats.totalTranslationTime > 0
                                                            ? formatProcessingTime(currentStats.totalTranslationTime)
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} sm={6} md={2}>
                                    <Card
                                        elevation={1}
                                        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                    >
                                        <CardContent
                                            sx={{
                                                flex: '1 0 auto',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography variant="subtitle2" color="text.secondary">
                                                Total Time
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                End-to-End Duration
                                            </Typography>

                                            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Avg:
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {currentStats.avgProcessingTime > 0
                                                            ? formatProcessingTime(currentStats.avgProcessingTime)
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        mt: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="caption" color="text.secondary">
                                                        Total:
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {currentStats.totalE2ETime > 0
                                                            ? formatProcessingTime(currentStats.totalE2ETime)
                                                            : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Total Duration Card - only show for S2TT and T2TT */}
                                {(activeTab === 's2tt' || activeTab === 't2tt') && (
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Card
                                            elevation={1}
                                            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                        >
                                            <CardContent
                                                sx={{
                                                    flex: '1 0 auto',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Total Duration
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    All Videos Combined
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {formatTotalDuration(currentStats.totalDuration)}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Total Size Card - only show for T2TT and Text */}
                                {(activeTab === 't2tt' || activeTab === 'text') && (
                                    <Grid item xs={12} sm={6} md={2}>
                                        <Card
                                            elevation={1}
                                            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                                        >
                                            <CardContent
                                                sx={{
                                                    flex: '1 0 auto',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Total Size
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    All Files Combined
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                                                    <Typography variant="body1" fontWeight="medium">
                                                        {formatFileSize(currentStats.totalFileSize)}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>

                        {/* Activity Chart */}
                        <Paper sx={{ p: 2, mb: 3, width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle1" fontWeight="medium">
                                    Activity Over Time
                                </Typography>
                                <ToggleButtonGroup
                                    value={graphGranularity}
                                    exclusive
                                    onChange={(e, val) => { if (val) setGraphGranularity(val); }}
                                    size="small"
                                >
                                    <ToggleButton value="days">Days</ToggleButton>
                                    <ToggleButton value="weeks">Weeks</ToggleButton>
                                    <ToggleButton value="months">Months</ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            {chartData.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                    No data available for chart
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {/* Items chart - full width for dubbing, half width when secondary chart shown */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                            Items
                                        </Typography>
                                        <ResponsiveContainer width="100%" height={200}>
                                            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                                                <RechartsTooltip />
                                                <Legend />
                                                <Bar dataKey="inProgress" name="In Progress" fill="#1976d2" radius={[3, 3, 0, 0]} />
                                                <Bar dataKey="completed" name="Completed" fill="#2e7d32" radius={[3, 3, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>

                                    {(activeTab === 's2tt' || activeTab === 't2tt') && (
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                Total Duration
                                            </Typography>
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                                    <YAxis
                                                        tick={{ fontSize: 11 }}
                                                        width={40}
                                                        tickFormatter={(v) => v >= 60 ? `${Math.floor(v / 60)}h` : `${v}m`}
                                                    />
                                                    <RechartsTooltip
                                                        formatter={(value) => [
                                                            value >= 60
                                                                ? `${Math.floor(value / 60)}h ${value % 60}m`
                                                                : `${value}m`,
                                                            'Duration',
                                                        ]}
                                                    />
                                                    <Bar dataKey="totalDurationMin" name="Duration" fill="#7b1fa2" radius={[3, 3, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    )}

                                    {activeTab === 'text' && (
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                Total Size (MB)
                                            </Typography>
                                            <ResponsiveContainer width="100%" height={200}>
                                                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                                    <YAxis
                                                        tick={{ fontSize: 11 }}
                                                        width={50}
                                                        tickFormatter={(v) => `${v}MB`}
                                                    />
                                                    <RechartsTooltip formatter={(value) => [`${value} MB`, 'Size']} />
                                                    <Bar dataKey="totalSizeMB" name="Size" fill="#e65100" radius={[3, 3, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : videos.length === 0 ? (
                            <Paper
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    bgcolor: darkMode ? 'background.paper' : undefined,
                                }}
                                className="processing-queue-empty"
                            >
                                {activeTab === 'text' && (
                                    <>
                                        <TextSnippetIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No text-only transcription data found
                                        </Typography>
                                    </>
                                )}
                                {activeTab === 's2tt' && (
                                    <>
                                        <MovieIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No speech-to-text video processing data found
                                        </Typography>
                                    </>
                                )}
                                {activeTab === 't2tt' && (
                                    <>
                                        <TranslateIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No text-to-text video translation data found
                                        </Typography>
                                    </>
                                )}
                                {activeTab === 'dubbing' && (
                                    <>
                                        <GraphicEqIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No dubbing transformation data found
                                        </Typography>
                                    </>
                                )}
                                {activeTab === 'book' && (
                                    <>
                                        <MenuBookIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                        <Typography variant="h6" color="text.secondary">
                                            No book translation data found
                                        </Typography>
                                    </>
                                )}
                                <Typography variant="body2" color="text.secondary">
                                    Try changing the timeframe or status filter
                                </Typography>
                            </Paper>
                        ) : (
                            <>
                                <TableContainer component={Paper} sx={{ width: '100%' }}>
                                    <Table
                                        sx={{
                                            width: '100%',
                                            tableLayout: 'fixed', // This helps with column width distribution
                                            '& .MuiTableRow-root:hover': {
                                                backgroundColor: darkMode
                                                    ? 'rgba(255, 255, 255, 0.04)'
                                                    : 'rgba(0, 0, 0, 0.04)',
                                            },
                                            '& .MuiTableHead-root .MuiTableCell-root': {
                                                backgroundColor: darkMode ? 'background.paper' : 'background.default',
                                            },
                                        }}
                                    >
                                        <TableHead>{renderTableHeader()}</TableHead>
                                        <TableBody>{renderTableBody()}</TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    component="div"
                                    count={videos.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </>
                        )}
                    </Box>

                    {/* Video processing timeline */}
                    {selectedVideo && (
                        <Paper sx={{ p: 3, mb: 3, width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Processing Timeline: {selectedVideo.name}</Typography>
                                <Button size="small" onClick={() => setSelectedVideo(null)}>
                                    Close
                                </Button>
                            </Box>

                            <Timeline position="alternate">
                                {/* Processing Started */}
                                <TimelineItem>
                                    <TimelineSeparator>
                                        <TimelineDot color="primary">
                                            <MovieIcon fontSize="small" />
                                        </TimelineDot>
                                        <TimelineConnector />
                                    </TimelineSeparator>
                                    <TimelineContent>
                                        <Typography variant="body2" fontWeight="medium">
                                            Processing Started
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDateTime(selectedVideo.processingStartedAt)}
                                        </Typography>
                                    </TimelineContent>
                                </TimelineItem>

                                {/* Video Processing Phase */}
                                <TimelineItem>
                                    <TimelineSeparator>
                                        <TimelineDot
                                            color={selectedVideo.compressionStatus === 'FAILED' ? 'error' : 'secondary'}
                                        >
                                            {selectedVideo.compressionStatus === 'FAILED' ? (
                                                <ErrorIcon fontSize="small" />
                                            ) : (
                                                <AccessTimeIcon fontSize="small" />
                                            )}
                                        </TimelineDot>
                                        <TimelineConnector />
                                    </TimelineSeparator>
                                    <TimelineContent>
                                        <Typography variant="body2" fontWeight="medium">
                                            Video Processing Phase{' '}
                                            {selectedVideo.compressionStatus === 'FAILED' ? '(Failed)' : ''}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Duration: {formatProcessingTime(selectedVideo.videoProcessingTimeMinutes)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            (Download and compression)
                                        </Typography>
                                        {selectedVideo.compressionStatus === 'FAILED' && (
                                            <Typography variant="caption" color="error" display="block">
                                                Compression Failed
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Video Duration: {formatDuration(selectedVideo.duration)}
                                        </Typography>
                                    </TimelineContent>
                                </TimelineItem>

                                {/* Translation Phase Header */}
                                <TimelineItem>
                                    <TimelineSeparator>
                                        <TimelineDot color="info">
                                            <TranslateIcon fontSize="small" />
                                        </TimelineDot>
                                        <TimelineConnector />
                                    </TimelineSeparator>
                                    <TimelineContent>
                                        <Typography variant="body2" fontWeight="medium">
                                            Translation Phase
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Average Duration:{' '}
                                            {formatProcessingTime(selectedVideo.translationTimeMinutes)}
                                        </Typography>
                                    </TimelineContent>
                                </TimelineItem>

                                {/* Individual Languages */}
                                {selectedVideo.languages &&
                                    selectedVideo.languages.map((lang, idx) => (
                                        <TimelineItem key={idx}>
                                            <TimelineSeparator>
                                                <TimelineDot color={lang.status === 'READY' ? 'success' : 'warning'}>
                                                    {lang.status === 'READY' ? (
                                                        <CheckCircleIcon fontSize="small" />
                                                    ) : (
                                                        <AccessTimeIcon fontSize="small" />
                                                    )}
                                                </TimelineDot>
                                                {idx < selectedVideo.languages.length - 1 && <TimelineConnector />}
                                            </TimelineSeparator>
                                            <TimelineContent>
                                                {renderLanguageStatus(selectedVideo, lang)}

                                                <Typography variant="caption" color="text.secondary">
                                                    Started: {formatDateTime(lang.translationStartedAt)}
                                                </Typography>

                                                {lang.translationCompletedAt ? (
                                                    <>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            display="block"
                                                        >
                                                            Completed: {formatDateTime(lang.translationCompletedAt)}
                                                        </Typography>
                                                        <Typography variant="caption" color="primary" display="block">
                                                            Duration:{' '}
                                                            {formatProcessingTime(lang.translationTimeMinutes)}
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ ml: 1 }}
                                                            >
                                                                (Speech-to-text and translations)
                                                            </Typography>
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    lang.translationStartedAt && (
                                                        <>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                display="block"
                                                            >
                                                                In progress: {getTimeSince(lang.translationStartedAt)}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="primary"
                                                                display="block"
                                                            >
                                                                Elapsed:{' '}
                                                                {formatProcessingTime(lang.translationTimeMinutes)}
                                                            </Typography>
                                                        </>
                                                    )
                                                )}
                                            </TimelineContent>
                                        </TimelineItem>
                                    ))}

                                {/* Processing Completed (show only if completed) */}
                                {selectedVideo.processingCompletedAt ? (
                                    <TimelineItem>
                                        <TimelineSeparator>
                                            <TimelineDot color="success">
                                                <CheckCircleIcon fontSize="small" />
                                            </TimelineDot>
                                        </TimelineSeparator>
                                        <TimelineContent>
                                            <Typography variant="body2" fontWeight="medium">
                                                Processing Completed
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDateTime(selectedVideo.processingCompletedAt)}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    mt: 1,
                                                    p: 1,
                                                    bgcolor: darkMode ? 'background.paper' : 'background.default',
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    <strong>Video Processing:</strong>{' '}
                                                    {formatProcessingTime(selectedVideo.videoProcessingTimeMinutes)}
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                        (Download and compression)
                                                    </Typography>
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    <strong>Translation (Avg):</strong>{' '}
                                                    {formatProcessingTime(selectedVideo.translationTimeMinutes)}
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                        (Speech-to-text and translations)
                                                    </Typography>
                                                </Typography>
                                                <Typography variant="caption" color="primary" display="block">
                                                    <strong>Total Processing Time:</strong>{' '}
                                                    {formatProcessingTime(selectedVideo.totalProcessingTimeMinutes)}
                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                        (End-to-end duration)
                                                    </Typography>
                                                </Typography>
                                            </Box>
                                        </TimelineContent>
                                    </TimelineItem>
                                ) : (
                                    <TimelineItem>
                                        <TimelineSeparator>
                                            <TimelineDot color="primary">
                                                <AccessTimeIcon fontSize="small" />
                                            </TimelineDot>
                                        </TimelineSeparator>
                                        <TimelineContent>
                                            <Typography variant="body2" fontWeight="medium">
                                                Processing In Progress
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Elapsed:{' '}
                                                {formatProcessingTime(selectedVideo.totalProcessingTimeMinutes)}
                                            </Typography>
                                        </TimelineContent>
                                    </TimelineItem>
                                )}
                            </Timeline>
                        </Paper>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ProcessingStatusDashboard;
